import Workspace from 'iam/models/Workspace'
import { Permission } from 'iam/dtos/permissions'
import TeamMemberStore from 'iam/TeamMemberStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { fromStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'
import { delay, map, startWith, switchMap } from 'rxjs/operators'
import { of, Subject, Subscription } from 'rxjs'
import orderBy from 'lodash/orderBy'
import { IWorkspaceSearcher } from 'components/WorkspaceSelectorViewHeading'
import { task } from 'mobx-task'

/**
 * View model for managing the firm dashboard workspace selector.
 */
export default class FirmDashboardWorkspaceSearchViewModel
  implements IWorkspaceSearcher
{
  /**
   * User is searching a workspace.
   */
  @observable
  editing = false

  /**
   * Workspace to filter by.
   */
  @observable
  workspace: Workspace = Workspace.none

  /**
   * Observable subscription to searches$ used for disposing on deactivate.
   */
  private searchSubscription = new Subscription()

  /**
   * Search text changes.
   */
  private searchTextChange = new Subject<string>()

  /**
   * The search text as a computed, derived from the search text change stream.
   */
  private getSearchText = fromStream(
    '',
    this.searchTextChange.pipe(map((value) => value)),
  )

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  searches$ = // When the search filter changes
    this.searchTextChange.pipe(
      switchMap((change) =>
        // Apply the delay if debouncing
        of(change).pipe(delay(300)),
      ),
      startWith(''),
      // Make sure to only subscribe once.
      shareLatest(),
    )

  /**
   * Constructor
   */
  constructor(
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private teamMemberStore: TeamMemberStore,
  ) {
    makeObservable(this)
  }

  /**
   * Show workspace searcher if user can view dashboard in 5 or more workspaces.
   */
  @computed
  get showWorkspaceSearcher() {
    return this.workspacesWhereUserCanViewDashboard.length >= 5
  }

  /**
   * Search text computed getter.
   */
  @computed
  get searchText() {
    return this.getSearchText()
  }

  /**
   * Whether the current user have the permission
   * to view the Dashboard on the workspaces he's member of.
   */
  @computed
  get workspacesWhereUserCanViewDashboard() {
    return orderBy(
      this.getCurrentUserWorkspaceMemberships()
        .filter(({ member, workspace }) => {
          return (
            member.hasPermission('VIEW_FIRM_DASHBOARD') ||
            this.getTeamMembersInWorkspaceWhereUserHasSomePermissions(
              workspace,
              ['VIEW_FIRM_DASHBOARD'],
            ).length > 0
          )
        })
        .map((m) => m.workspace),
      (ws) => ws.name.toLowerCase(),
      'asc',
    )
  }

  /**
   * Filtered workspaces.
   */
  @computed
  get filteredWorkspaces() {
    const result = this.workspacesWhereUserCanViewDashboard

    if (this.searchText) {
      const searchText = this.searchText.toLowerCase()

      return result.filter((ws) =>
        ws.name.toLocaleLowerCase().includes(searchText),
      )
    }
    return result
  }

  /**
   * Sets the search text.
   *
   * @param searchText
   */
  @action.bound
  setSearchText(searchText: string) {
    this.searchTextChange.next(searchText)
  }

  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    this.searchSubscription = this.searches$.subscribe()
  })

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.searchSubscription.unsubscribe()
  }

  /**
   * Gets the teams in the specified workspace that the user is a member of,
   * and where the user has some of the given permissions.
   *
   * @param workspace
   * @param permissions
   */
  private getTeamMembersInWorkspaceWhereUserHasSomePermissions(
    workspace: Workspace,
    permissions: Permission[],
  ) {
    const teamMembers = this.teamMemberStore.teamMembersForWorkspaceMember(
      workspace.id,
    )
    return teamMembers.filter((tm) => tm.hasSomePermissions(...permissions))
  }
}
